import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, Search, Calendar, Filter, X } from 'lucide-react';

import { useEvents } from '@/api/hooks/useEvents';
import { EventCard } from '@/components/events/EventCard';
import type { EventFilters, ProcessingStatus } from '@/api/types/event.types';

const STATUS_OPTIONS: Array<{ value: ProcessingStatus; labelKey: string }> = [
  { value: 'draft', labelKey: 'events.status.draft' },
  { value: 'uploading', labelKey: 'events.status.uploading' },
  { value: 'processing', labelKey: 'events.status.processing' },
  { value: 'completed', labelKey: 'events.status.completed' },
  { value: 'failed', labelKey: 'events.status.failed' },
];

export function EventListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<EventFilters>({});
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isError } = useEvents(filters);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }));
  };

  const handleStatusFilter = (status: ProcessingStatus | undefined) => {
    setFilters((prev) => ({ ...prev, processing_status: status, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchInput('');
    setShowFilters(false);
  };

  const hasActiveFilters = !!(filters.processing_status || filters.search);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="page-title">{t('nav.events')}</h1>
        <Link to="/events/create" className="btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {t('events.createEvent')}
        </Link>
      </div>

      {/* Search & Filter Bar */}
      <div className="mt-6 space-y-3">
        <div className="flex gap-2">
          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('events.searchPlaceholder')}
                className="input-field ps-9"
              />
            </div>
            <button type="submit" className="btn-primary">
              {t('actions.search')}
            </button>
          </form>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary inline-flex items-center gap-2 ${
              hasActiveFilters ? 'border-primary-500 text-primary-600 dark:border-primary-400 dark:text-primary-400' : ''
            }`}
          >
            <Filter className="h-4 w-4" />
            {t('actions.filter')}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="card flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('events.filterByStatus')}:
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleStatusFilter(undefined)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  !filters.processing_status
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                }`}
              >
                {t('events.allStatuses')}
              </button>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleStatusFilter(opt.value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    filters.processing_status === opt.value
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="ms-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-3 w-3" />
                {t('events.clearFilters')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="mt-6">
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        )}

        {isError && (
          <div className="card text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{t('status.error')}</p>
          </div>
        )}

        {!isLoading && !isError && data && data.results.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-16 dark:border-gray-600">
            <Calendar className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              {hasActiveFilters ? t('status.noResults') : t('events.emptyTitle')}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilters ? t('events.tryDifferentFilters') : t('events.emptyDescription')}
            </p>
            {!hasActiveFilters && (
              <Link to="/events/create" className="btn-primary mt-6 inline-flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t('events.createFirstEvent')}
              </Link>
            )}
          </div>
        )}

        {!isLoading && !isError && data && data.results.length > 0 && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.results.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>

            {/* Pagination */}
            {(data.next || data.previous) && (
              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  disabled={!data.previous}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      page: (prev.page || 2) - 1,
                    }))
                  }
                  className="btn-secondary"
                >
                  {t('actions.previous')}
                </button>
                <button
                  type="button"
                  disabled={!data.next}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      page: (prev.page || 1) + 1,
                    }))
                  }
                  className="btn-secondary"
                >
                  {t('actions.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
